import React, { useState, useEffect } from 'react';
import { Search, Loader2, Eye, X, BookOpen, Calendar, User, FileText, CheckCircle2 } from 'lucide-react';

export default function CiaAcknowledgementsList() {
  const [dataList, setDataList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableLoading, setTableLoading] = useState(false);
  const [selectedAck, setSelectedAck] = useState(null); // For detail view modal

  // Pagination Controls
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    fetchAcknowledgements();
  }, [searchTerm]);

  const fetchAcknowledgements = async () => {
    setTableLoading(true);
    try {
      const res = await fetch(
        `https://metapi.emedhatechnologies.com/api/exams/cia-acknowledgements?search=${encodeURIComponent(searchTerm)}`
      );
      const result = await res.json();
      if (result.success) setDataList(result.data);
      else setDataList([]);
    } catch (err) {
      console.error('Failed to load CIA acknowledgements:', err);
      setDataList([]);
    } finally {
      setTableLoading(false);
    }
  };

  // Pagination Window Range Computation
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dataList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(dataList.length / itemsPerPage);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div style={styles.container}>
      {/* STEP 1: SEARCH FILTER BAR */}
      <div style={styles.filterControlPanel}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by candidate, enrollment no, course, semester..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={styles.tableSearchInput}
          />
        </div>
        <div style={styles.totalIndicator}>
          Total Acknowledgements: <strong style={{ color: '#1c6ec9' }}>{dataList.length}</strong>
        </div>
      </div>

      {/* STEP 2: DATA TABLE */}
      {tableLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#1c6ec9" />
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <table style={styles.dataTable}>
            <thead>
              <tr>
                <th style={styles.tableTh}>Enrolment No</th>
                <th style={styles.tableTh}>Candidate Name</th>
                <th style={styles.tableTh}>Course Stream</th>
                <th style={styles.tableTh}>Course Code</th>
                <th style={styles.tableTh}>Term / Semester</th>
                <th style={styles.tableTh}>Admission Year</th>
                <th style={styles.tableTh}>Papers Submitted</th>
                <th style={styles.tableTh}>Submission Date</th>
                <th style={styles.tableTh}>Status</th>
                <th style={styles.tableTh}>Action</th>
              </tr>
            </thead>
            <tbody>
              {dataList.length === 0 ? (
                <tr>
                  <td colSpan="10" style={styles.noDataTd}>
                    No CIA submission acknowledgements found matching your search.
                  </td>
                </tr>
              ) : (
                currentItems.map((item) => (
                  <tr key={item.id} style={styles.tableTr}>
                    <td style={{ ...styles.tableTd, fontWeight: '700', color: '#1e40af' }}>
                      {item.enrolment_number}
                    </td>
                    <td style={{ ...styles.tableTd, fontWeight: '700', color: '#0f172a' }}>
                      {item.candidate_name}
                    </td>
                    <td style={{ ...styles.tableTd, color: '#334155', fontWeight: '600' }}>
                      {item.course_name}
                    </td>
                    <td style={{ ...styles.tableTd, color: '#64748b', fontWeight: '600' }}>
                      {item.course_code}
                    </td>
                    <td style={styles.tableTd}>
                      <span style={styles.termLabel}>{item.semester || 'Semester 1'}</span>
                    </td>
                    <td style={{ ...styles.tableTd, color: '#475569', fontSize: '13px' }}>
                      {item.year_of_admission}
                    </td>
                    <td style={{ ...styles.tableTd, textAlign: 'center', fontWeight: '700', color: '#2563eb' }}>
                      {item.no_of_papers_submitted || (item.papers_list ? item.papers_list.length : 0)} Papers
                    </td>
                    <td style={{ ...styles.tableTd, fontSize: '12.5px', color: '#475569' }}>
                      {item.submission_date || formatDate(item.created_at)}
                    </td>
                    <td style={styles.tableTd}>
                      <span
                        style={{
                          ...styles.statusPill,
                          ...(item.status === 'Approved'
                            ? styles.statusApproved
                            : item.status === 'Verified'
                            ? styles.statusVerified
                            : styles.statusSubmitted),
                        }}
                      >
                        {item.status || 'Submitted'}
                      </span>
                    </td>
                    <td style={styles.tableTd}>
                      <button
                        onClick={() => setSelectedAck(item)}
                        style={styles.viewRowBtn}
                        title="View Full Acknowledgement"
                      >
                        <Eye size={13} style={{ marginRight: '4px' }} /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* STEP 3: PAGINATION */}
      {dataList.length > 0 && (
        <div style={styles.paginationDock}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            style={currentPage === 1 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Previous
          </button>
          <span style={styles.pageIndicator}>
            Page <strong>{currentPage}</strong> of {totalPages || 1}
          </span>
          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            style={
              currentPage === totalPages || totalPages === 0
                ? { ...styles.pageBtn, ...styles.pageBtnDisabled }
                : styles.pageBtn
            }
          >
            Next
          </button>
        </div>
      )}

      {/* STEP 4: DETAILED ACKNOWLEDGEMENT VIEW MODAL */}
      {selectedAck && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalWrapperCard}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                  CIA Submission Acknowledgement
                </h3>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Record ID: #{selectedAck.id} • Submitted: {selectedAck.submission_date || formatDate(selectedAck.created_at)}
                </span>
              </div>
              <button onClick={() => setSelectedAck(null)} style={styles.modalCloseX}>
                <X size={18} />
              </button>
            </div>

            <div style={styles.modalBodyScroll}>
              {/* Candidate Info Grid Banner */}
              <div style={styles.applicantInfoBanner}>
                <div style={styles.infoCol}>
                  <span style={styles.infoLabel}>Candidate Name</span>
                  <span style={styles.infoValue}>{selectedAck.candidate_name}</span>
                </div>
                <div style={styles.infoCol}>
                  <span style={styles.infoLabel}>Enrolment Number</span>
                  <span style={{ ...styles.infoValue, color: '#1e40af' }}>{selectedAck.enrolment_number}</span>
                </div>
                <div style={styles.infoCol}>
                  <span style={styles.infoLabel}>Admission Year</span>
                  <span style={styles.infoValue}>{selectedAck.year_of_admission}</span>
                </div>
                <div style={styles.infoCol}>
                  <span style={styles.infoLabel}>Status</span>
                  <span style={{ ...styles.infoValue, color: '#059669' }}>{selectedAck.status || 'Submitted'}</span>
                </div>
              </div>

              {/* Course Meta Banner */}
              <div style={styles.courseMetaBar}>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Course</span>
                  <div style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                    {selectedAck.course_name} ({selectedAck.course_code})
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Semester</span>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e40af' }}>
                    {selectedAck.semester}
                  </div>
                </div>
              </div>

              {/* Submitted Subject Papers Table */}
              <div style={{ marginTop: '16px' }}>
                <span style={{ fontSize: '12px', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase' }}>
                  Submitted Papers ({selectedAck.papers_list ? selectedAck.papers_list.length : 0} Papers)
                </span>
                <div style={styles.subjectsTableWrapper}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        <th style={{ ...styles.subTh, width: '90px' }}>Sub Code</th>
                        <th style={styles.subTh}>Title of the Course Paper</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!selectedAck.papers_list || selectedAck.papers_list.length === 0 ? (
                        <tr>
                          <td colSpan="2" style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
                            No papers list recorded.
                          </td>
                        </tr>
                      ) : (
                        selectedAck.papers_list.map((paper, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 12px', fontWeight: '700', color: '#2563eb', fontSize: '12.5px' }}>
                              {paper.code}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#334155', fontSize: '12.5px' }}>
                              {paper.title}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Remarks Box */}
              {selectedAck.remarks ? (
                <div style={styles.remarksBox}>
                  <span style={styles.infoLabel}>Remarks</span>
                  <div style={{ fontSize: '12.5px', color: '#334155', marginTop: '2px' }}>
                    {selectedAck.remarks}
                  </div>
                </div>
              ) : null}

              {/* Modal Footer Close Action */}
              <div style={styles.modalActionDock}>
                <button onClick={() => setSelectedAck(null)} style={styles.modalCloseBtn}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES MATRIX
const styles = {
  container: { width: '100%', boxSizing: 'border-box' },
  filterControlPanel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  searchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    maxWidth: '420px',
    width: '100%',
  },
  searchIcon: { position: 'absolute', left: '12px', color: '#94a3b8' },
  tableSearchInput: {
    width: '100%',
    padding: '10px 12px 10px 38px',
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    color: '#0f172a',
    fontSize: '13.5px',
    outline: 'none',
    boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
  },
  totalIndicator: { fontSize: '13.5px', fontWeight: '600', color: '#475569' },
  dataTable: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', background: '#ffffff' },
  tableTh: {
    padding: '12px 14px',
    borderBottom: '2px solid #e2e8f0',
    background: '#f8fafc',
    color: '#64748b',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  tableTr: { borderBottom: '1px solid #f1f5f9' },
  tableTd: { padding: '13px 14px', color: '#475569', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' },
  noDataTd: { padding: '40px 14px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', fontWeight: '500' },
  termLabel: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1e40af',
    padding: '3px 8px',
    borderRadius: '6px',
    fontSize: '11.5px',
    fontWeight: '700',
  },
  statusPill: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', display: 'inline-block' },
  statusSubmitted: { background: '#fef3c7', color: '#92400e' },
  statusVerified: { background: '#e0e7ff', color: '#3730a3' },
  statusApproved: { background: '#d1fae5', color: '#065f46' },
  viewRowBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1d4ed8',
    padding: '5px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  paginationDock: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '12px',
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid #edf2f7',
  },
  pageIndicator: { fontSize: '13px', color: '#475569' },
  pageBtn: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '6px 14px',
    color: '#334155',
    fontSize: '12.5px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  pageBtnDisabled: { background: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed', borderColor: '#e2e8f0' },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(6px)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  modalWrapperCard: {
    background: '#ffffff',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '650px',
    maxHeight: '90vh',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: '14px',
    borderBottom: '1px solid #e2e8f0',
  },
  modalCloseX: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  modalBodyScroll: { overflowY: 'auto', marginTop: '14px', paddingRight: '4px' },
  applicantInfoBanner: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    background: '#f8fafc',
    borderRadius: '12px',
    padding: '14px',
    border: '1px solid #e2e8f0',
  },
  infoCol: { display: 'flex', flexDirection: 'column' },
  infoLabel: { fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' },
  infoValue: { fontSize: '13px', fontWeight: '700', color: '#0f172a', marginTop: '2px' },
  courseMetaBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '12px',
    padding: '12px 14px',
    marginTop: '12px',
  },
  remarksBox: {
    background: '#ffffff',
    borderRadius: '10px',
    padding: '12px',
    border: '1px solid #e2e8f0',
    marginTop: '12px',
  },
  subjectsTableWrapper: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden',
    marginTop: '8px',
  },
  subTh: { padding: '8px 12px', textAlign: 'left', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' },
  modalActionDock: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '20px',
    paddingTop: '14px',
    borderTop: '1px solid #e2e8f0',
  },
  modalCloseBtn: {
    background: '#2563eb',
    border: 'none',
    color: '#ffffff',
    padding: '8px 18px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
  },
};